import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { ColumnId } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { createPriceValidationMessage } from '@/domain/pricing/utils/validation';
import { createStyles } from '@/contexts/theme-context';
import { Icons } from '@usepepper/sprout-kit-ui';
import {
  OrderEntryInputCellShortcutCallbacks,
  useOrderEntryCellShortcuts,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-order-entry-cell-shortcuts';
import { Platform, TextInput, View } from 'react-native';
import { PRICE_INPUT_WIDTH } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-constants';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { PriceEditType } from '@/types';
import { PriceOverrideInfo } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { PriceOverrideInfoDisplay } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/price-override-info';
import {
  QuickAction,
  QuickActionMenu,
} from '@/experiences/sales-portal/components/quick-action-menu';
import { SalesPortalKeyboardScope } from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { useCellKeyboardNavigation } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-cell-keyboard-navigation';
import { useCellQuickActions } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-cell-quick-actions';
import { useDebouncedCallback } from 'use-debounce';
import { usePriceEditorState } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-editor-state';
import { usePricingCellIcon } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-pricing-cell-icon';
import { usePricingStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/pricing-store';
import { useRafThrottle } from '@experiences/hooks/use-raf-throttle';
import { useTemporaryAdjustmentQuickAction } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/use-temporary-adjustment-quick-action';

/** Allow an optional leading minus, digits, and a single decimal point (e.g. "12", "-5", "-12.3", ".5") */
const GROSS_PROFIT_INPUT_REGEX = /^-?\d*\.?\d*$/;

const REVERT_ICON_SIZE = 16;

const INPUT_HEIGHT = 32;
const INPUT_MIN_WIDTH = 48;
const VALIDATION_DEBOUNCE_MS = 300;

interface OrderEntryTableGrossProfitCellProps {
  canSetManualOverrideExpiration?: boolean;
  grossProfitDisplayString: string;
  onSave: (editor: PriceEditor, variantPackUUID: string) => void;
  onValidationError: (message: string) => void;
  priceOverrideInfo?: PriceOverrideInfo;
  /**
   * Rendered index (sorted position) passed from extended cell context.
   */
  rowIndex: number;
  shortcutCallbacks?: OrderEntryInputCellShortcutCallbacks;
  variantPackUUID: string;
}

export const OrderEntryTableGrossProfitCell =
  React.memo<OrderEntryTableGrossProfitCellProps>(
    ({
      canSetManualOverrideExpiration,
      grossProfitDisplayString,
      onSave,
      onValidationError,
      priceOverrideInfo,
      rowIndex,
      shortcutCallbacks,
      variantPackUUID,
    }) => {
      const [styles, theme] = useStyles();
      const throttle = useRafThrottle();

      const editor = usePricingStore.getState().editors.get(variantPackUUID);
      const editorState = usePriceEditorState(variantPackUUID);

      // canWrite=true because the screen-level isPriceEditingEnabled already
      // verified WRITE permission — this checks editor-level flags only (cost data, VP settings)
      const isEditable = useMemo(
        () => editor?.isPriceEditingEnabled(true) ?? false,
        [editor],
      );
      const isPeakPriceProtected = useMemo(
        () => editor?.isPeakPriceProtectionInEffect() ?? false,
        [editor],
      );

      const isSaving = editor?.isSaveInProgress() ?? false;
      const disabled = !editor || !editorState || !isEditable || isSaving;

      const handleShortcutKeyPress =
        useOrderEntryCellShortcuts(shortcutCallbacks);

      // On native iOS, editable={false} prevents the TextInput from receiving
      // Tab focus via external keyboard. Keep it editable so Tab navigation
      // works, and use showSoftInputOnFocus to suppress the keyboard instead.
      // TODO(SRG-162, SRG-164): Follow up with product — this allows characters
      // to be entered into a disabled cell on iOS. handleChangeText guards against
      // persisting edits, but the visual behavior is confusing.
      const nativeEditable = Platform.OS !== 'web' || !disabled;

      const {
        handleBlurFocusCleanup,
        handleFocus,
        handleKeyPress: handleNavigationKeyPress,
        inputRef,
        isCellFocused,
      } = useCellKeyboardNavigation({
        columnId: ColumnId.GpPercentage,
        onPreKeyPress: handleShortcutKeyPress,
        rowIndex,
      });

      const defaultGpText =
        editor?.getDefaultGrossProfitDisplayText() ||
        grossProfitDisplayString.replace('%', '');

      const hasOverride = priceOverrideInfo?.activeOverride !== undefined;

      const temporaryAdjustmentAction = useTemporaryAdjustmentQuickAction({
        editor,
        hasActiveOverride: hasOverride,
        isDirty: editorState?.isDirty ?? false,
        isEnabled: !!canSetManualOverrideExpiration && !disabled,
      });

      const quickActions = useMemo<QuickAction[]>(() => {
        if (disabled) {
          return [];
        }
        return [
          ...(temporaryAdjustmentAction ? [temporaryAdjustmentAction] : []),
          {
            detail: defaultGpText ? `${defaultGpText}%` : undefined,
            icon: (
              <Icons.RotateCcw
                color={theme.sk.color.text.primary}
                size={REVERT_ICON_SIZE}
              />
            ),
            key: 'revert',
            label: i18n.t('sales_portal.order_entry.quick_actions.revert'),
            onSelect: () => {
              editor.revertToDefaultPrice();
            },
          },
        ];
      }, [
        defaultGpText,
        disabled,
        editor,
        temporaryAdjustmentAction,
        theme.sk.color.text.primary,
      ]);

      const {
        handleKeyPress,
        handleQuickActionSelect,
        isEditing,
        isQuickActionsOpen,
        isQuickActionsOpenRef,
        saveAndReset,
        setIsEditing,
      } = useCellQuickActions({
        disabled,
        editor,
        handleStandardKeyPress: handleNavigationKeyPress,
        inputRef,
        onSave,
        quickActions,
        throttle,
        variantPackUUID,
      });

      // Debounced inline validation — shows error icon while typing
      const [validationMessage, setValidationMessage] = useState<string | null>(
        null,
      );

      // Derive: clear validation when editor resets (e.g. after clearEdits)
      const activeValidationMessage = editorState?.isDirty
        ? validationMessage
        : null;

      const debouncedValidate = useDebouncedCallback(() => {
        if (!editor) {
          return;
        }
        const result = editor.validate(editor.getState());
        if (!result.valid && result.reason) {
          setValidationMessage(
            createPriceValidationMessage(result.reason, result.bound),
          );
        } else {
          setValidationMessage(null);
        }
      }, VALIDATION_DEBOUNCE_MS);

      const handleChangeText = useCallback(
        (text: string) => {
          if (disabled) {
            return;
          }
          if (text && !GROSS_PROFIT_INPUT_REGEX.test(text)) {
            return;
          }
          if (text) {
            const parsed = Number(text);
            if (!Number.isNaN(parsed) && parsed >= 100) {
              return;
            }
          }
          setIsEditing(true);
          editor?.updateGrossProfit(text);
          debouncedValidate();
        },
        [debouncedValidate, disabled, editor, setIsEditing],
      );

      const handleBlur = useCallback(async () => {
        if (isQuickActionsOpenRef.current) {
          return;
        }
        handleBlurFocusCleanup();

        if (!editor) {
          setIsEditing(false);
          return;
        }

        const currentState = editor.getState();
        if (currentState.isDirty) {
          // Check validation before save — revert + toast on failure
          const validation = editor.validate(currentState);
          if (!validation.valid && validation.reason) {
            const msg = createPriceValidationMessage(
              validation.reason,
              validation.bound,
            );
            editor.clearEdits();
            setValidationMessage(null);
            if (msg) {
              onValidationError(msg);
            }
            setIsEditing(false);
            return;
          }
          await onSave(editor, variantPackUUID);
        }
        setValidationMessage(null);
        setIsEditing(false);
      }, [
        editor,
        handleBlurFocusCleanup,
        isQuickActionsOpenRef,
        onSave,
        onValidationError,
        setIsEditing,
        variantPackUUID,
      ]);

      // When actively editing or dirty, show raw editor text (allows clearing).
      // Otherwise, fall back to collection's default GP%.
      const strippedGp = grossProfitDisplayString.replace('%', '');
      const isUserEditing = isEditing || editorState?.isDirty;
      const displayValue = isUserEditing
        ? (editorState?.grossProfitDisplayText ?? '')
        : editorState?.grossProfitDisplayText || strippedGp;

      const icon = usePricingCellIcon({
        activeValidationMessage,
        disabled,
        isCellFocused,
        isPeakPriceProtected,
        isSaving,
        quickActionsLength: quickActions.length,
        theme,
      });

      const grossProfitInput = (
        <View
          style={[
            styles.grossProfitInput,
            isCellFocused && styles.grossProfitInputFocused,
            disabled && styles.grossProfitInputDisabled,
          ]}
        >
          {icon ? <View style={styles.iconSlot}>{icon}</View> : null}
          <TextInput
            editable={nativeEditable}
            keyboardType="decimal-pad"
            onBlur={handleBlur}
            onChangeText={handleChangeText}
            onFocus={handleFocus}
            onKeyPress={handleKeyPress}
            ref={inputRef}
            selectTextOnFocus
            showSoftInputOnFocus={!disabled}
            style={[styles.inputText, disabled && styles.inputTextDisabled]}
            value={displayValue}
          />
        </View>
      );

      return (
        <View
          style={[styles.cellContainer, hasOverride && styles.overrideCell]}
        >
          <QuickActionMenu
            actions={quickActions}
            isOpen={isQuickActionsOpen}
            keyboardScope={SalesPortalKeyboardScope.QUICK_ACTION_MENU}
            onActionSelect={handleQuickActionSelect}
            onClose={saveAndReset}
            trigger={grossProfitInput}
          />
          {hasOverride && priceOverrideInfo ? (
            <PriceOverrideInfoDisplay
              defaultValue={priceOverrideInfo.defaultGrossProfitDisplay}
              editedByName={priceOverrideInfo.editedByName}
              expiryDateDisplay={priceOverrideInfo.expiryDateDisplay}
              lastValueLabelKey="sales_portal.order_entry.table.temporary_adjustment.last_gross_profit"
              showOverrideIndicator={
                priceOverrideInfo.activeOverride === PriceEditType.GrossProfit
              }
              showTemporaryIndicator={
                !!canSetManualOverrideExpiration &&
                priceOverrideInfo.isTemporaryOverride
              }
            />
          ) : null}
        </View>
      );
    },
  );

const useStyles = createStyles((theme) => ({
  cellContainer: {
    flex: 1,
  },
  grossProfitInput: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    height: INPUT_HEIGHT,
    minWidth: INPUT_MIN_WIDTH,
    overflow: 'hidden',
    paddingLeft: theme.sk.spacing.spaceXsmall,
    paddingRight: theme.sk.spacing.spaceSMedium,
    width: PRICE_INPUT_WIDTH,
  },
  grossProfitInputDisabled: {
    backgroundColor: theme.sk.color.bg.primary.disabled,
  },
  grossProfitInputFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
  iconSlot: {
    alignItems: 'center',
    flexShrink: 0,
    justifyContent: 'center',
    width: theme.sk.spacing.spaceMedium,
  },
  inputText: {
    ...theme.sk.typography.input.md,
    backgroundColor: 'transparent',
    borderWidth: 0,
    color: theme.sk.color.text.primary,
    flex: 1,
    minWidth: 0,
    padding: 0,
    textAlign: 'right',
  },
  inputTextDisabled: {
    color: theme.sk.color.text.disabled,
  },
  overrideCell: {
    minHeight: theme.sk.spacing.space3xlarge,
  },
}));
